<template>
  <div class="material-check-page frame-page" :class="{ 'is-loading': loading }">
    <div class="page-intro">
      <p>结算清单请选 SAP ZMM020「详细清单」；竣工材料表请选项目材料汇总表。支持点击选择与拖拽上传。</p>
    </div>

    <input ref="settlementInput" type="file" hidden accept=".mht,.mhtml,.xls,.xlsx" @change="onSettlementSelected" />
    <input ref="materialInput" type="file" hidden accept=".xlsx,.xls,.mht,.mhtml" @change="onMaterialSelected" />

    <section class="panel">
      <div class="panel-title">
        <span class="step">1</span>
        <span>结算清单</span>
        <span v-if="settlementPath" class="tag tag-done">已上传</span>
        <span v-else class="tag tag-muted">待选择</span>
      </div>
      <div
        class="drop-zone"
        :class="{
          'is-empty': !settlementPath,
          'is-dragover': settlementDragOver,
          'is-filled': settlementPath,
        }"
        @click="pickSettlement"
        @dragenter.prevent="setDragOver('settlement', true)"
        @dragover.prevent="setDragOver('settlement', true)"
        @dragleave.prevent="setDragOver('settlement', false)"
        @drop.prevent="onDropSettlement"
      >
        <div class="drop-zone-body">
          <div class="drop-icon" :class="{ 'is-uploaded': settlementPath }">
            <span v-if="settlementPath" class="drop-check">✓</span>
            <span v-else>清单</span>
          </div>
          <div class="drop-text">
            <div class="drop-title" :class="{ placeholder: !settlementPath }">
              {{ settlementPath || '选择或拖拽结算清单文件' }}
            </div>
            <div class="drop-hint">
              <template v-if="settlementPath">
                已读取 {{ settlementRowCount }} 行 · {{ settlementPath }}
              </template>
              <template v-else>ZMM020 详细清单 · .mht .mhtml .xls .xlsx</template>
            </div>
          </div>
          <div class="drop-actions" @click.stop>
            <Button v-if="settlementPath" size="s" @click="previewSettlement">预览</Button>
            <Button size="s" class="drop-btn-clear" @click="clearSettlement">
              清除
            </Button>
            <Button size="s" color="primary" @click="pickSettlement">浏览</Button>
          </div>
        </div>
      </div>

      <div v-if="projects.length" class="project-row">
        <span class="field-label">项目</span>
        <div class="project-select-wrap">
          <ProjectSelect
            v-model="projectChoice"
            :options="projectOptions"
            :all-projects-label="ALL_PROJECTS"
            placeholder="选择项目"
            @change="onProjectChange"
          />
          <div v-if="currentProjectTitle" class="project-selected-meta project-selected-name">
            {{ currentProjectTitle }}
          </div>
          <div v-if="currentProjectPrefix" class="project-selected-meta">
            WBS：{{ currentProjectPrefix }}
          </div>
        </div>
        <span class="hint">识别到 {{ projects.length }} 个项目</span>
      </div>
    </section>

    <section class="panel">
      <div class="panel-title">
        <span class="step">2</span>
        <span>竣工材料表</span>
        <span v-if="materialPath" class="tag tag-done">已上传</span>
        <span v-else class="tag tag-muted">待选择</span>
      </div>
      <div
        class="drop-zone"
        :class="{
          'is-empty': !materialPath,
          'is-dragover': materialDragOver,
          'is-filled': materialPath,
        }"
        @click="pickMaterial"
        @dragenter.prevent="setDragOver('material', true)"
        @dragover.prevent="setDragOver('material', true)"
        @dragleave.prevent="setDragOver('material', false)"
        @drop.prevent="onDropMaterial"
      >
        <div class="drop-zone-body">
          <div class="drop-icon" :class="{ 'is-uploaded': materialPath }">
            <span v-if="materialPath" class="drop-check">✓</span>
            <span v-else>材料</span>
          </div>
          <div class="drop-text">
            <div class="drop-title" :class="{ placeholder: !materialPath }">
              {{ materialPath || '选择或拖拽竣工材料表' }}
            </div>
            <div class="drop-hint">
              <template v-if="materialPath">
                已读取 {{ materialRowCount }} 行 · 工作表 {{ materialSheetName }}
              </template>
              <template v-else>项目材料汇总 · .xlsx .xls</template>
            </div>
          </div>
          <div class="drop-actions" @click.stop>
            <Button v-if="materialPath" size="s" @click="previewMaterial">预览</Button>
            <Button size="s" class="drop-btn-clear" @click="clearMaterial">
              清除
            </Button>
            <Button size="s" color="primary" @click="pickMaterial">浏览</Button>
          </div>
        </div>
      </div>

      <div v-if="materialStatus" class="material-status">{{ materialStatus }}</div>

      <div v-if="materialSheets.length" class="mapping-panel">
        <div class="mapping-toggle" @click="mappingOpen = !mappingOpen">
          <span>工作表与列映射</span>
          <span>{{ mappingOpen ? '▲' : '▼' }}</span>
        </div>
        <div v-show="mappingOpen" class="mapping-body">
          <Form mode="block" labelWidth="88">
            <FormItem label="工作表">
              <Select
                v-model="materialSheetName"
                :datas="sheetOptions"
                style="width: 100%"
                @change="onSheetChange"
              />
            </FormItem>
            <div class="mapping-grid">
              <FormItem label="物料编号">
                <Select
                  v-model="mapping.material_number_col"
                  :datas="currentColumns"
                  style="width: 100%"
                  @change="saveMaterialConfig"
                />
              </FormItem>
              <FormItem label="物料描述">
                <Select
                  v-model="mapping.material_description_col"
                  :datas="currentColumns"
                  style="width: 100%"
                  @change="saveMaterialConfig"
                />
              </FormItem>
              <FormItem label="单位">
                <Select
                  v-model="mapping.unit_col"
                  :datas="currentColumns"
                  style="width: 100%"
                  @change="saveMaterialConfig"
                />
              </FormItem>
              <FormItem label="数量">
                <Select
                  v-model="mapping.quantity_col"
                  :datas="currentColumns"
                  style="width: 100%"
                  @change="saveMaterialConfig"
                />
              </FormItem>
            </div>
          </Form>
        </div>
      </div>
    </section>

    <section class="panel actions-panel">
      <div class="panel-title">
        <span class="step">3</span>
        <span>生成结果</span>
      </div>
      <div class="actions">
        <Button size="l" :disabled="!settlementPath" @click="doSum">数量求和</Button>
        <Button size="l" :disabled="!settlementPath" @click="doConditionalSum">条件求和</Button>
        <Button size="l" color="primary" :disabled="!settlementPath || !materialPath" @click="doCheck">
          生成核对结果
        </Button>
        <Button size="l" text @click="doReset">清空选择</Button>
      </div>
      <p v-if="actionsHint" class="actions-hint">{{ actionsHint }}</p>
    </section>

    <div class="page-footer-note">
      说明：结算清单需含 WBS元素、物料编号、物料描述、单位、数量、批次号、项目描述；材料表需含物料编号、物料描述、数量、单位。
    </div>

    <Modal
      v-model="previewVisible"
      :title="previewTitle"
      className="preview-modal"
      :middle="true"
      hasDivider
      :hasCloseIcon="true"
      :closeOnMask="true"
    >
      <div class="preview-meta">
        <span>共 {{ previewTotalRows }} 行</span>
        <span v-if="previewMeta"> · {{ previewMeta }}</span>
        <span> · 当前预览前 {{ previewRows.length }} 行</span>
      </div>
      <div class="preview-table-wrap">
        <Table
          :datas="previewRows"
          :columns="previewTableColumns"
          :border="true"
          :isStripe="true"
          :height="300"
        />
      </div>
      <template #footer>
        <Button color="primary" @click="previewVisible = false">关闭</Button>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { confirm, message } from 'heyui';
import { materialCheckApi as api } from '@/api/materialCheck';
import ProjectSelect from '@/components/ProjectSelect.vue';

const ALL_PROJECTS = '全部项目';
const SETTLEMENT_EXTS = ['.mht', '.mhtml', '.xls', '.xlsx'];
const MATERIAL_EXTS = ['.xlsx', '.xls', '.mht', '.mhtml'];
const SETTLEMENT_FORMAT_ERROR =
  '请选择 SAP ZMM020 导出的「详细清单」，当前文件不是可用的详细清单格式。';

function getProjectTitle(project) {
  if (!project) return '';
  const prefix = (project.prefix || '').trim();
  let raw = (project.description || project.label || '').trim();
  if (!raw) return '';
  if (prefix && raw.endsWith(prefix)) {
    raw = raw.slice(0, -prefix.length).trim();
  }
  const parts = raw.split(/\s+/);
  const last = parts[parts.length - 1] || '';
  if (/^\d{10,}$/.test(last)) {
    raw = parts.slice(0, -1).join(' ');
  }
  return raw;
}

const loading = ref(false);
const settlementPath = ref('');
const materialPath = ref('');
const settlementRowCount = ref(0);
const materialRowCount = ref(0);
const projects = ref([]);
const projectChoice = ref(ALL_PROJECTS);
const materialSheets = ref([]);
const materialSheetName = ref('');
const mapping = ref({
  material_number_col: '',
  material_description_col: '',
  unit_col: '',
  quantity_col: '',
});
const mappingOpen = ref(false);
const settlementInput = ref(null);
const materialInput = ref(null);
const settlementDragOver = ref(false);
const materialDragOver = ref(false);
const previewVisible = ref(false);
const previewTitle = ref('文件预览');
const previewMeta = ref('');
const previewColumns = ref([]);
const previewRows = ref([]);
const previewTotalRows = ref(0);

const projectOptions = computed(() => {
  if (!projects.value.length) return [];
  const items = projects.value.map((p) => ({
    key: p.label,
    title: getProjectTitle(p),
    wbs: p.prefix || '',
  }));
  if (projects.value.length === 1) return items;
  return [{ key: ALL_PROJECTS, title: ALL_PROJECTS, prefix: '' }, ...items];
});

const currentProjectPrefix = computed(() => {
  if (!projectChoice.value || projectChoice.value === ALL_PROJECTS) return '';
  const project = projects.value.find((p) => p.label === projectChoice.value);
  return project?.prefix || '';
});

const currentProjectTitle = computed(() => {
  if (!projectChoice.value || projectChoice.value === ALL_PROJECTS) return '';
  const project = projects.value.find((p) => p.label === projectChoice.value);
  return getProjectTitle(project);
});

const currentSheet = computed(() =>
  materialSheets.value.find((s) => s.sheetName === materialSheetName.value)
);

const currentColumns = computed(() => currentSheet.value?.columns || []);

const sheetOptions = computed(() =>
  materialSheets.value.map((s) => ({
    key: s.sheetName,
    title: `${s.sheetName}（${s.rowCount} 行，评分 ${s.score}）`,
  }))
);

const materialStatus = computed(() => {
  if (!materialPath.value || !materialSheetName.value) return '';
  return `工作表：${materialSheetName.value} · 列映射已自动识别`;
});

const actionsHint = computed(() => {
  if (!settlementPath.value) return '请先上传结算清单，再执行求和或核对';
  if (!materialPath.value) return '已上传结算清单；上传竣工材料表后可生成核对结果';
  return '';
});

const previewTableColumns = computed(() => {
  const rows = previewRows.value;
  return previewColumns.value.map((col) => ({
    title: col,
    prop: col,
    width: buildPreviewColumnWidth(col, rows),
  }));
});

function buildPreviewColumnWidth(col, rows) {
  const isWideCol = /WBS元素描述|物料描述|项目描述|订单描述|工厂名称/.test(col);
  const isMidCol = /WBS元素|批次号|凭证编号|物料编号|保留号/.test(col);
  if (isWideCol) {
    const maxLen = rows.reduce(
      (max, row) => Math.max(max, String(row[col] ?? '').length),
      col.length
    );
    return Math.min(480, Math.max(260, maxLen * 8 + 32));
  }
  if (isMidCol) {
    return 160;
  }
  return Math.max(72, Math.min(112, col.length * 12 + 28));
}

function formatPreviewProjectMeta(choice) {
  if (!choice || choice === ALL_PROJECTS) return '全部项目';
  const project = projects.value.find((p) => p.label === choice);
  const title = getProjectTitle(project);
  const wbs = project?.prefix;
  if (title && wbs) return `${title}（WBS：${wbs}）`;
  return title || choice;
}

function matchExtension(fileName, extensions) {
  const lower = fileName.toLowerCase();
  return extensions.some((ext) => lower.endsWith(ext));
}

async function withLoading(fn) {
  loading.value = true;
  try {
    return await fn();
  } finally {
    loading.value = false;
  }
}

function showError(msg) {
  message.error(msg || '操作失败');
}

function showSettlementFormatError(msg) {
  const text = msg || SETTLEMENT_FORMAT_ERROR;
  confirm({
    title: '结算清单格式不正确',
    content: `${text}<br><br>请选择 SAP ZMM020 导出的「详细清单」，支持 .mht .mhtml .xls .xlsx。<br>汇总表、出库表等其它类型不能用于核对。`,
  });
}

function showSuccess(msg) {
  message.success(msg, 2000);
}

function applySettlementResult(data) {
  settlementPath.value = data.displayName || '';
  settlementRowCount.value = data.rowCount || 0;
  projects.value = data.projects || [];
  projectChoice.value = data.projectChoice || ALL_PROJECTS;
}

function applyMaterialResult(data) {
  materialPath.value = data.displayName || '';
  materialRowCount.value =
    data.sheets?.find((s) => s.sheetName === data.sheetName)?.rowCount || 0;
  materialSheets.value = data.sheets || [];
  materialSheetName.value = data.sheetName || '';
  mapping.value = { ...(data.mapping || mapping.value) };
}

function setDragOver(type, active) {
  if (type === 'settlement') settlementDragOver.value = active;
  else materialDragOver.value = active;
}

function pickSettlement() {
  settlementInput.value?.click();
}

function pickMaterial() {
  materialInput.value?.click();
}

async function uploadSettlementFile(file) {
  if (!file) return;
  if (!matchExtension(file.name, SETTLEMENT_EXTS)) {
    showSettlementFormatError('文件扩展名不正确，结算清单仅支持 .mht .mhtml .xls .xlsx');
    return;
  }
  const res = await withLoading(() => api.uploadSettlement(file));
  if (!res.ok) {
    showSettlementFormatError(res.error);
    return;
  }
  applySettlementResult(res);
  await nextTick();
  showSuccess(`已读取结算清单，共 ${res.rowCount} 行`);
}

async function uploadMaterialFile(file) {
  if (!file) return;
  if (!matchExtension(file.name, MATERIAL_EXTS)) {
    showError('竣工材料表仅支持 .xlsx .xls .mht .mhtml');
    return;
  }
  const res = await withLoading(() => api.uploadMaterial(file));
  if (!res.ok) return showError(res.error);
  applyMaterialResult(res);
  await nextTick();
  showSuccess(`已读取材料表工作表：${res.sheetName}`);
}

async function onSettlementSelected(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  await uploadSettlementFile(file);
}

async function onMaterialSelected(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  await uploadMaterialFile(file);
}

async function onDropSettlement(event) {
  setDragOver('settlement', false);
  await uploadSettlementFile(event.dataTransfer?.files?.[0]);
}

async function onDropMaterial(event) {
  setDragOver('material', false);
  await uploadMaterialFile(event.dataTransfer?.files?.[0]);
}

async function onProjectChange() {
  const res = await api.setProject(projectChoice.value);
  if (!res.ok) showError(res.error);
}

async function onSheetChange() {
  const res = await api.configureMaterial(materialSheetName.value);
  if (!res.ok) return showError(res.error);
  mapping.value = { ...res.mapping };
}

async function saveMaterialConfig() {
  const res = await api.configureMaterial(materialSheetName.value, mapping.value);
  if (!res.ok) showError(res.error);
}

async function doSum() {
  const res = await withLoading(() => api.generateSumReport());
  if (!res.ok) return showError(res.error);
  showSuccess(`汇总完成：${res.path}`);
}

async function doConditionalSum() {
  const res = await withLoading(() => api.generateConditionalSumReport());
  if (!res.ok) return showError(res.error);
  showSuccess(`条件汇总完成：${res.path}`);
}

async function doCheck() {
  const res = await withLoading(() => api.generateCheckReport());
  if (!res.ok) return showError(res.error);
  showSuccess(`核对完成：${res.path}`);
}

async function openPreview(type) {
  const isSettlement = type === 'settlement';
  const res = await withLoading(() =>
    isSettlement ? api.previewSettlement() : api.previewMaterial()
  );
  if (!res.ok) return showError(res.error);
  previewTitle.value = isSettlement
    ? `结算清单预览 · ${res.fileName || ''}`
    : `竣工材料表预览 · ${res.fileName || ''}`;
  previewMeta.value = isSettlement
    ? `项目：${formatPreviewProjectMeta(res.projectChoice)}`
    : `工作表：${res.sheetName || ''}`;
  previewColumns.value = res.columns || [];
  previewRows.value = res.rows || [];
  previewTotalRows.value = res.totalRows || 0;
  previewVisible.value = true;
}

async function previewSettlement() {
  await openPreview('settlement');
}

async function previewMaterial() {
  await openPreview('material');
}

async function clearSettlement() {
  const res = await api.clearSettlement();
  if (!res.ok) return showError(res.error);
  settlementPath.value = '';
  settlementRowCount.value = 0;
  projects.value = [];
  projectChoice.value = ALL_PROJECTS;
  showSuccess('已清除结算清单');
}

async function clearMaterial() {
  const res = await api.clearMaterial();
  if (!res.ok) return showError(res.error);
  materialPath.value = '';
  materialRowCount.value = 0;
  materialSheets.value = [];
  materialSheetName.value = '';
  mapping.value = {
    material_number_col: '',
    material_description_col: '',
    unit_col: '',
    quantity_col: '',
  };
  showSuccess('已清除竣工材料表');
}

async function doReset() {
  const res = await api.resetAll();
  if (!res.ok) return showError(res.error);
  settlementPath.value = '';
  settlementRowCount.value = 0;
  materialPath.value = '';
  materialRowCount.value = 0;
  projects.value = [];
  projectChoice.value = ALL_PROJECTS;
  materialSheets.value = [];
  materialSheetName.value = '';
  mapping.value = {
    material_number_col: '',
    material_description_col: '',
    unit_col: '',
    quantity_col: '',
  };
}

onMounted(async () => {
  const res = await api.getState();
  if (res.ok) {
    settlementPath.value = res.settlementDisplayName || '';
    settlementRowCount.value = res.settlementRowCount || 0;
    materialPath.value = res.materialDisplayName || '';
    materialRowCount.value = res.materialRowCount || 0;
    projects.value = res.projects || [];
    projectChoice.value = res.projectChoice || ALL_PROJECTS;
    materialSheets.value = res.materialSheets || [];
    materialSheetName.value = res.materialSheetName || '';
    if (res.materialMapping) mapping.value = { ...res.materialMapping };
  }
});
</script>

<style lang="less" scoped>
@import './material-check.less';
</style>

<style lang="less">
.preview-modal.h-modal {
  .h-notify-container {
    width: min(960px, 96vw) !important;
    max-width: min(960px, 96vw) !important;
  }

  .h-notify-body {
    padding-top: 12px;
    padding-bottom: 12px;
    overflow: hidden;
  }

  .preview-meta {
    margin-bottom: 10px;
    font-size: 12px;
    color: #8b95a5;
    line-height: 1.6;
    word-break: break-word;
  }

  .preview-table-wrap {
    overflow-x: auto;
  }

  .h-table {
    font-size: 12px;
  }

  .h-table table {
    table-layout: auto !important;
    width: max-content !important;
    min-width: 100%;
  }

  .h-table th,
  .h-table td {
    height: auto !important;
    min-height: 40px;
    max-width: 480px;
    overflow: visible !important;
    text-overflow: unset !important;
    white-space: normal !important;
    word-break: break-word;
    line-height: 1.5;
    vertical-align: top;
  }

  .h-table .h-table-th-title {
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: unset !important;
    line-height: 1.4;
  }
}
</style>
